import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import {
  Card,
  Upload,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Button,
  notification,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";

import { apiGetEventContent, apiCheckEventCode } from "api/api";

import { convertTimeStamp } from "utility/functions/convertTimestamp";

import axios from "axios";

import {
  timeZoneOptions,
  tagsOptions,
  countryOptions,
  cityOptions,
} from "utility/options/index";
import cities from "utility/options/taiwan_all_county.json";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(utc);
dayjs.extend(timezone);

const EmptyUploadState = () => (
  <div className="flex h-full w-full items-center justify-center text-Gray-5">
    尚未上傳圖片
  </div>
);

const UploadDisplay = ({ file }) => {
  const [objectUrl, setObjectUrl] = useState("");
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);

    if (file?.originFileObj instanceof Blob) {
      const nextObjectUrl = URL.createObjectURL(file.originFileObj);
      setObjectUrl(nextObjectUrl);

      return () => {
        URL.revokeObjectURL(nextObjectUrl);
      };
    }

    setObjectUrl("");
  }, [file]);

  const src = file?.url || file?.thumbUrl || objectUrl;

  if (!src || hasError) {
    return <EmptyUploadState />;
  }

  return (
    <div className="h-full w-full overflow-hidden">
      <img
        src={src}
        alt={file?.name || "cover"}
        className="h-full w-full object-contain"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

const getCacheBustedUrl = (url) =>
  typeof url === "string" && url
    ? `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`
    : url;

const createSingleUploadList = (fileList = []) => {
  const latestFile = fileList.slice(-1)[0];

  if (!latestFile) {
    return [];
  }

  return [
    {
      ...latestFile,
      uid: latestFile.uid || `${Date.now()}`,
      name: latestFile.name || "cover",
      status: latestFile.status || "done",
    },
  ];
};

const BasicInfo = () => {
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  const requireMsg = "此欄位必填!";
  const isEmptyValue = (value) =>
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);
  const addressFieldNames = [
    ["address", "country"],
    ["address", "city"],
    ["address", "line"],
    ["address", "remark"],
  ];
  const requireIfAnyAddressFieldFilled = (label) => ({
    validator: async (_, value) => {
      const shouldValidate = addressFieldNames.some(
        (fieldName) => !isEmptyValue(form.getFieldValue(fieldName)),
      );

      if (!shouldValidate || !isEmptyValue(value)) {
        return Promise.resolve();
      }

      return Promise.reject(new Error(`${label}${requireMsg}`));
    },
  });
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const id = routeId || sessionStorage.target_id;

  const [isLoading, setIsLoading] = useState(true);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [mobileFileList, setMobileFileList] = useState([]);
  // # 活動時區
  const [eventTimezone, setEventTimezone] = useState("Asia/Taipei");

  // # 獲取資料
  const fetchData = async () => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await apiGetEventContent({ id });
      const { content } = res.data;
      const address = content.address || {};
      const socialLinks = Array.isArray(content.socialLinks)
        ? content.socialLinks
        : [];
      const tags = Array.isArray(content.tags)
        ? content.tags.filter(
            (tag) => tag !== undefined && tag !== "undefined" && tag !== "",
          )
        : [];
      const hasUploadUrl = (url) =>
        typeof url === "string" &&
        url.trim() !== "" &&
        url !== "undefined" &&
        url !== "null";
      const createUploadList = (url) =>
        hasUploadUrl(url)
          ? [
              {
                uid: url,
                name: url.split("/").pop() || "cover",
                status: "done",
                url: getCacheBustedUrl(url),
              },
            ]
          : [];
      const webCoverList = createUploadList(content.webCover);
      const mobileCoverList = createUploadList(content.mobileCover);
      // 設定活動時區（如果後端有儲存的話）
      if (content.timezone) {
        setEventTimezone(content.timezone);
      }
      setFileList(webCoverList);
      setMobileFileList(mobileCoverList);
      form.setFieldsValue({
        ...content,
        address,
        webCover: webCoverList,
        mobileCover: mobileCoverList,
        tags,
        socialLinkTitle: socialLinks[0]?.title || "",
        timezone: content.timezone || "Asia/Taipei",
        socialLinkUrl:
          socialLinks[0]?.url === "undefined" ? "" : socialLinks[0]?.url || "",
        dateTime:
          content.startDateTime && content.endDateTime
            ? [
                dayjs(convertTimeStamp(content.startDateTime)),
                dayjs(convertTimeStamp(content.endDateTime)),
              ]
            : undefined,
      });
    } catch (err) {
      console.log("err", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      sessionStorage.target_id = id;
    }
    fetchData();
  }, [id]);

  const { RangePicker } = DatePicker;

  // # 封面圖片更新時
  const coverProps = {
    accept: ".gif,.jpg,.png",
    name: "image",
    withCredentials: true,
    listType: "picture-card",
    maxCount: 1,
    beforeUpload: (file) => {
      setFileList(
        createSingleUploadList([
          {
            uid: file.uid || `${Date.now()}`,
            name: file.name,
            status: "done",
            originFileObj: file,
          },
        ]),
      );
      return false;
    },
    onChange({ fileList: newFileList }) {
      setFileList(createSingleUploadList(newFileList));
    },
  };

  // # 手機版封面圖片更新時
  const mobileCoverProps = {
    accept: ".gif,.jpg,.png",
    name: "image",
    withCredentials: true,
    listType: "picture-card",
    maxCount: 1,
    beforeUpload: (file) => {
      setMobileFileList(
        createSingleUploadList([
          {
            uid: file.uid || `${Date.now()}`,
            name: file.name,
            status: "done",
            originFileObj: file,
          },
        ]),
      );
      return false;
    },
    onChange({ fileList: newFileList }) {
      setMobileFileList(createSingleUploadList(newFileList));
    },
  };

  // * 標籤選擇
  const handleTagsChange = (value) => {
    console.log(`selected ${JSON.stringify(value || [])}`);
  };

  const normalizeUpload = (event) => {
    if (Array.isArray(event)) return event;
    return event?.fileList;
  };

  const getUploadFile = (value, fallbackList = []) => {
    const list = Array.isArray(value)
      ? value
      : Array.isArray(value?.fileList)
        ? value.fileList
        : fallbackList;

    return list?.[0]?.originFileObj || list?.[0]?.file || null;
  };

  // * 提交表單

  const handleUpdate = async (values) => {
    setIsBtnLoading(true);
    const token = sessionStorage.t;
    let bodyFormData = new FormData();
    const {
      webCover,
      mobileCover,
      socialLinkTitle,
      socialLinkUrl,
      address = {},
      dateTime = [],
      tags,
      title,
      code,
      timezone,
    } = values;
    const appendIfDefined = (key, value) => {
      if (value !== undefined && value !== null && value !== "") {
        bodyFormData.append(key, value);
      }
    };
    const webCoverFile = getUploadFile(webCover, fileList);
    const mobileCoverFile = getUploadFile(mobileCover, mobileFileList);

    // 使用表單中選擇的時區，如果沒有則使用 state 中的時區
    const selectedTimezone = timezone || eventTimezone;

    appendIfDefined("title", title);
    if (code) {
      bodyFormData.append("code", code);
    }
    (Array.isArray(tags) ? tags : tags ? [tags] : []).forEach((tag) => {
      bodyFormData.append("tags", tag);
    });
    appendIfDefined("address.country", address.country);
    appendIfDefined("address.city", address.city);
    appendIfDefined("address.line", address.line);
    appendIfDefined("address.link", address.link);
    appendIfDefined("address.remark", address.remark);
    appendIfDefined("timezone", selectedTimezone);
    // 根據選擇的活動時區來轉換日期為 UTC
    appendIfDefined(
      "startDateTime",
      dayjs.tz(dateTime[0]?.$d, selectedTimezone).utc().unix(),
    );
    appendIfDefined(
      "endDateTime",
      dayjs.tz(dateTime[1]?.$d, selectedTimezone).utc().unix(),
    );
    appendIfDefined("socialLink.title", socialLinkTitle);
    appendIfDefined("socialLink.url", socialLinkUrl);
    if (webCoverFile) {
      bodyFormData.append("webCover", webCoverFile);
    }
    if (mobileCoverFile) {
      bodyFormData.append("mobileCover", mobileCoverFile);
    }
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/Activity/${id}`,
        bodyFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      api.success({
        message: `更新成功`,
        description: `${res.data.message}`,
      });
      // fetchData();
      setTimeout(() => {
        navigate(0);
      }, 800);
    } catch (err) {
      api.error({
        message: `更新失敗`,
        description: "",
      });
      setTimeout(() => {
        fetchData();
      }, 500);

      console.log("失敗：：", err);
    } finally {
      setIsBtnLoading(false);
    }
  };

  const onFinish = (values) => {
    handleUpdate(values);
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  useEffect(() => {
    cities.map((item) => {
      item.label = item.name;
      item.value = item.CityEngName;
      cityOptions.push(item);
    });
  }, [cities]);

  return (
    <div className="w-full">
      {contextHolder}

      <h2 className="text-Gray-8 mb-0">活動基本資訊</h2>
      <p className="m-0 text-Gray-5">設定你的活動基本資訊。</p>
      <Card
        title={<h3>開始建立活動資訊</h3>}
        className="text-center mt-5 rounded-3xl px-10 sm:px-0"
        loading={isLoading}
      >
        <Form
          name="basic"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          layout="vertical"
          variant="filled"
          size="large"
          form={form}
          scrollToFirstError={true}
        >
          <Row gutter={16}>
            <Col
              xs={24}
              md={18}
              className="[&_.ant-form-item-control-input-content]:h-[400px] [&_.ant-upload]:h-[400px] [&_.ant-upload-list-item]:h-[400px] mb-6 md:mb-0"
            >
              <Form.Item
                name="webCover"
                label="桌機封面"
                valuePropName="fileList"
                getValueFromEvent={normalizeUpload}
              >
                <Upload
                  {...coverProps}
                  showUploadList={false}
                  className="h-full w-full [&_.ant-upload]:h-full [&_.ant-upload]:w-full [&_.ant-upload.ant-upload-select]:block [&_.ant-upload.ant-upload-select]:h-full [&_.ant-upload.ant-upload-select]:w-full [&_.ant-upload.ant-upload-select-picture-card]:m-0"
                  fileList={fileList}
                >
                  <UploadDisplay file={fileList?.[0]} />
                </Upload>
              </Form.Item>
            </Col>
            <Col
              xs={24}
              md={6}
              className="[&_.ant-form-item-control-input-content]:h-[400px] [&_.ant-upload]:h-[400px] [&_.ant-upload-list-item]:h-[400px]"
            >
              <Form.Item
                name="mobileCover"
                label="手機封面"
                valuePropName="fileList"
                getValueFromEvent={normalizeUpload}
              >
                <Upload
                  {...mobileCoverProps}
                  showUploadList={false}
                  className="h-full w-full [&_.ant-upload]:h-full [&_.ant-upload]:w-full [&_.ant-upload.ant-upload-select]:block [&_.ant-upload.ant-upload-select]:h-full [&_.ant-upload.ant-upload-select]:w-full [&_.ant-upload.ant-upload-select-picture-card]:m-0"
                  fileList={mobileFileList}
                >
                  <UploadDisplay file={mobileFileList?.[0]} />
                </Upload>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="活動名稱"
            name="title"
            rules={[
              {
                required: true,
                message: requireMsg,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="客製化網址" required style={{ marginBottom: 0 }}>
            <div className="flex flex-wrap gap-2 items-start">
              <Form.Item
                name="code"
                className="mb-0 flex-1 min-w-0 text-left"
                validateTrigger="onBlur"
                rules={[
                  {
                    required: true,
                    message: requireMsg,
                  },
                  {
                    validator: async (_, value) => {
                      if (!value) return Promise.resolve();
                      try {
                        const res = await apiCheckEventCode({
                          id: id || 0,
                          code: value,
                        });
                        if (res.data?.content === false) {
                          return Promise.reject(new Error("此網址已重複"));
                        }
                        return Promise.resolve();
                      } catch (err) {
                        return Promise.reject(
                          new Error(
                            err.response?.data?.message ||
                              "驗證失敗，請稍後再試",
                          ),
                        );
                      }
                    },
                  },
                ]}
              >
                <Input placeholder="請輸入客製化網址" />
              </Form.Item>
              <Button
                type="primary"
                onClick={() => {
                  const code = form.getFieldValue("code");
                  if (code) {
                    const host = (
                      process.env.REACT_APP_WEB_DOMAIN || window.location.origin
                    ).replace(/\/+$/, "");
                    const shareUrl = `${host}/activity/${code}`;
                    navigator.clipboard.writeText(shareUrl);
                    api.success({
                      message: "成功複製連結",
                      description: shareUrl,
                    });
                  } else {
                    api.warning({
                      message: "請先輸入客製化網址",
                    });
                  }
                }}
              >
                分享
              </Button>
            </div>
          </Form.Item>
          <Form.Item
            label="活動時區"
            name="timezone"
            initialValue="Asia/Taipei"
          >
            <Select
              options={timeZoneOptions}
              className="text-left"
              onChange={(value) => setEventTimezone(value)}
              placeholder="選擇活動所在時區"
            />
          </Form.Item>
          <Form.Item
            label="活動時間 (輸入格式：YYYY-MM-DD HH:mm)"
            name="dateTime"
            rules={[
              {
                type: "array",
                required: true,
                message: requireMsg,
              },
              {
                validator: async (_, value) => {
                  if (!value || value.length !== 2) return Promise.resolve();

                  const [startDateTime, endDateTime] = value;
                  const today = dayjs();

                  if (
                    !dayjs(startDateTime).isAfter(today, "day") ||
                    !dayjs(endDateTime).isAfter(today, "day")
                  ) {
                    return Promise.reject(new Error("活動時間需大於今天"));
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              className="w-full"
              placeholder={["YYYY-MM-DD HH:mm", "YYYY-MM-DD HH:mm"]}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            />
          </Form.Item>
          <Form.Item label="活動標籤" name="tags">
            <Select
              mode="tags"
              style={{
                width: "100%",
              }}
              className="text-left [&_.ant-select-selection-item]:bg-Deep-Blue [&_.ant-select-selection-item]:text-white"
              placeholder="輸入按下Enter新增標籤"
              onChange={handleTagsChange}
              options={tagsOptions}
            />
          </Form.Item>
          <Form.Item label="相關連結" name="socialLinkTitle">
            <Input placeholder="顯示名稱：如官網、官方粉絲團" />
          </Form.Item>
          <Form.Item noStyle name="socialLinkUrl">
            <Input placeholder="https:www.google.com.tw/" />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="活動地點"
                name={["address", "country"]}
                className="mb-0"
                dependencies={addressFieldNames}
                rules={[requireIfAnyAddressFieldFilled("活動地點")]}
              >
                <Select
                  options={countryOptions}
                  placeholder="國家"
                  className="text-left"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                noStyle
                name={["address", "city"]}
                className="mb-0"
                dependencies={addressFieldNames}
                rules={[requireIfAnyAddressFieldFilled("活動地點")]}
              >
                <Select
                  options={cityOptions}
                  placeholder="城市"
                  className="text-left md:mt-12 mt-4 w-full"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={
              <p className="text-Gray-5">
                詳細地址（鄉鎮市區、道路、街名、巷弄號、樓層）
              </p>
            }
            name={["address", "line"]}
            className="mb-0"
            dependencies={addressFieldNames}
            rules={[requireIfAnyAddressFieldFilled("詳細地址")]}
          >
            <Input placeholder="南京東路四段100號10樓" />
          </Form.Item>
          <Form.Item
            label="Google Map Url"
            name={["address", "link"]}
            className="mb-0"
          >
            <Input placeholder="https://maps.app.goo.gl/WJ32jmVBy5oLCHB1A" />
          </Form.Item>
          <Form.Item
            label={<p className="text-Gray-5">補充說明</p>}
            name={["address", "remark"]}
            dependencies={addressFieldNames}
            rules={[requireIfAnyAddressFieldFilled("補充說明")]}
          >
            <Input placeholder="例如: 7-11對面.." />
          </Form.Item>
          <Form.Item>
            <Button
              className="w-full"
              htmlType="submit"
              type="primary"
              loading={isBtnLoading}
            >
              儲存
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default BasicInfo;
